import { CheckIcon, EyeIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { useMemo, useState } from 'react'
import {
  getTrinityCommentsLiens,
  getTrinityProgressStatus,
  postTrinityComments,
  postTrinityDocuments,
  sendTrinityReportToDocStorage,
} from 'services'
import { Checkbox, CopyableText } from 'stories/components'
import { formatDate, formatTime, getPrice2decimal } from 'utils'

import { AppraisalVendorModal } from './AppraisalVendorModal'

export const Trinity = ({ data, onClose }: { data: Record<string, any>; onClose: Function }) => {
  const [progress, setProgress] = useState<any>(null)
  const [reports, setReports] = useState<Array<any>>([])
  const [lienItems, setLienItems] = useState<Array<any>>([])
  const [selectedItems, setSelectedItems] = useState<Array<number>>([])

  const { orderID } = data.thirdPartyAPI

  const onMount = async () => {
    const res = await getTrinityProgressStatus(orderID, data.id)
    if (res.success) {
      setProgress(res.status)
      setReports(res.reports)
    }
  }

  const sendToDocStorage = async (item: any, index: number) => {
    if (item.sent) return
    const res = await sendTrinityReportToDocStorage(data.id, item)
    if (res.success) {
      setReports((prev) => {
        const temp = cloneDeep(prev)
        temp[index] = { ...item, sent: true }
        return temp
      })
    }
  }

  const onChangeLienItem = (index: number) => {
    setSelectedItems((prev) => {
      const temp = cloneDeep(prev)
      const pos = temp.indexOf(index)
      if (pos === -1) temp.push(index)
      else temp.splice(pos, 1)
      return temp.sort((a, b) => a - b)
    })
  }

  const renderProgressSection =
    progress !== null ? (
      <div className="px-2">
        <div className="mb-4">
          <div className="flex flex-wrap gap-4 justify-between">
            <div>
              Status: <span className="font-bold text-shade-blue italic">{progress}</span>
            </div>
          </div>
        </div>
      </div>
    ) : null

  const renderExtraTabContent = (menu: string) => {
    if (menu === 'Trinity Reports') {
      return (
        <div className="overflow-auto">
          <table className="w-full min-w-[620px] rounded border text-[14px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 border">No</th>
                <th className="p-2 border">Name</th>
                <th className="p-2 border">Type</th>
                <th className="p-2 border">Date</th>
                <th className="p-2 border">Action</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((item: any, index: number) => (
                <tr key={index}>
                  <td className="p-2 border">{index + 1}</td>
                  <td className="p-2 border">{item.fileName}</td>
                  <td className="p-2 border">{item.group.name}</td>
                  <td className="p-2 border">{formatDate(item.createdAt)}</td>
                  <td className="p-2 border">
                    <span className="flex flex-wrap gap-2 items-center">
                      <span className="text-shade-blue p-1 hover-shadow1 rounded cursor-pointer">
                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                          <EyeIcon className="w-4 h-4" />
                        </a>
                      </span>
                      <span
                        className="text-[13px] p-1 hover-shadow1 rounded cursor-pointer flex gap-1 items-center"
                        onClick={() => sendToDocStorage(item, index)}
                      >
                        Send to DocStorage
                        {item.sent ? <CheckIcon className="w-4 h-4" /> : ''}
                      </span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
    if (menu === 'Lien Items') {
      return (
        <div className="overflow-auto">
          <table className="w-full min-w-[820px] rounded border text-[14px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 border">No</th>
                <th className="p-2 border">Description</th>
                <th className="p-2 border">Cost</th>
                <th className="p-2 border">Amount Completed</th>
                <th className="p-2 border">Remark</th>
                <th className="p-2 border">Total % Complete this Item</th>
                <th className="p-2 border">% Complete this inspection</th>
              </tr>
            </thead>
            <tbody>
              {lienItems.map((item, index) => (
                <tr key={index} className={`${index % 2 ? 'bg-slate-50' : ''}`}>
                  <td className="p-1 border">{index + 1}</td>
                  <td className="p-1 border">
                    <Checkbox
                      size={3}
                      id={`Description-${index}`}
                      title={item.description}
                      key={`Description-${index}`}
                      value={selectedItems.includes(index)}
                      onChange={() => onChangeLienItem(index)}
                    />
                  </td>
                  <td className="p-1 border text-right">{getPrice2decimal(item.itemCost, false, true)}</td>
                  <td className="p-1 border text-right">
                    {getPrice2decimal((item.itemCost * item.percentCompleted) / 100, false, true)}
                  </td>
                  <td className="p-1 border">{item.remarks}</td>
                  <td className="p-1 border text-right">{item.previousPercentCompleted}</td>
                  <td className="p-1 border text-right">{item.percentCompleted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
    return null
  }

  const allSelectedItemText = useMemo(
    () => selectedItems.map((pos) => `${pos + 1}. ${lienItems[pos]?.description}`).join('\n'),
    [selectedItems, lienItems],
  )

  const renderAfterTabs = selectedItems.length ? (
    <div className="mt-4 flex flex-col gap-1 border-b text-[14px] sm:flex-row">
      <div className="mr-2 min-w-[72px]">Selected Liens ({selectedItems.length}):</div>
      <CopyableText copyText={allSelectedItemText}>
        <div>
          {selectedItems.map((pos, index) => (
            <span className="mr-2" key={pos}>
              {index !== 0 && '|'} {pos + 1}. <span className="italic">{lienItems[pos]?.description}</span>
            </span>
          ))}
        </div>
      </CopyableText>
    </div>
  ) : null

  return (
    <AppraisalVendorModal
      title={`Trinity - ${orderID}`}
      data={data}
      onClose={onClose}
      fetchLogs={() => getTrinityCommentsLiens(orderID, data.id)}
      mapLogs={(res) =>
        res.comments.map((item: any, index: number) => [
          index + 1,
          <div key={index}>
            {formatTime(item.time)}
            <br />
            <span className="italic">{item.by}</span>
          </div>,
          item.content,
        ])
      }
      onLogsLoaded={(res) => setLienItems(res.lineItems)}
      postComment={(comment) => postTrinityComments({ orderID, comment })}
      postDocuments={(id, documents) => postTrinityDocuments({ id, orderID, documents })}
      menuTabs={['Notification Logs', 'Documents', 'Trinity Reports', 'Lien Items']}
      sendMessageLabel="Send Message to Trinity"
      uploadDocumentsLabel="Upload Documents to Trinity"
      renderProgressSection={renderProgressSection}
      renderExtraTabContent={renderExtraTabContent}
      renderAfterTabs={renderAfterTabs}
      contentClassName="mt-4 max-h-[80vh] overflow-auto"
      onMount={onMount}
    />
  )
}
